import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { FiExternalLink, FiDownload, FiFileText, FiLink } from 'react-icons/fi';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { confirm } from '@/lib/confirm';
import { useSession } from '@/hooks/useSession';
import { isActivityStudentEvaluated } from '../activityStatusVariant';

// Tela de resposta do aluno do professor playful (rota
// /atividade-complementar/avaliar/:activityId/:id) — igual ao tema Default
// (ActivityEvaluationDefault em complementary-activity-evaluation/ActivityEvaluation.jsx):
// busca a atividade e o activity_student reais via API, e "Marcar como
// avaliado" grava um comentário de verdade (`corrected: true` +
// comments-attributes), em vez de só mexer no mock local.
export default function ActivityEvaluationDetailTeacher({ activityId, id, uri }) {
  const navigate = useNavigate();
  const client = useClient();
  const { session } = useSession();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityStudent, setActivityStudent] = useState(null);
  const [newComment, setNewComment] = useState('');

  const backRoute = `/atividade-complementar/avaliar-atividades/${activityId}`;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [activityResult, activityStudentResult] = await Promise.all([client.fetch(['activities', activityId]), client.fetch(['activities', activityId, 'activity_students', id])]);

      if (activityResult.error) {
        toast.error('Não foi possível carregar a atividade.');
      } else {
        setActivity(activityResult.data);
      }

      if (activityStudentResult.error) {
        toast.error('Não foi possível carregar a resposta do aluno.');
      } else {
        setActivityStudent(activityStudentResult.data);
      }

      setLoading(false);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId, id]);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      { route: '/atividade-complementar', name: 'Atividade Complementar', isActive: false },
      { route: backRoute, name: activity?.title || 'Avaliar atividade', isActive: false },
      { route: uri, name: activityStudent?.user?.name || 'Aluno', isActive: true }
    ]
  };

  if (loading) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <Loader />
      </DashboardContent>
    );
  }

  if (!activity || !activityStudent) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <div className="flex min-h-[50vh] items-center justify-center">
          <EmptyState type="data" />
        </div>
      </DashboardContent>
    );
  }

  const evaluated = isActivityStudentEvaluated(activityStudent);
  const attachment = activityStudent['activity-file'];
  const supportMaterial = activity['activity-file'];
  const supportMaterialUrl = supportMaterial?.url || supportMaterial?.file;
  // Mesmo critério do tema Default pra separar quem escreveu o quê (ver
  // comment.user.includes('Aluno') em complementary-activity-evaluation-view/Default/index.jsx):
  // a resposta do aluno é o(s) comentário(s) dele; o parecer do professor é
  // o resto.
  const comments = activityStudent.comments || [];
  const studentComments = comments.filter(comment => comment.user?.includes('Aluno'));
  const teacherComments = comments.filter(comment => !comment.user?.includes('Aluno'));

  const handleMarkEvaluated = async () => {
    const confirmed = await confirm({
      title: 'Tem certeza que deseja enviar a avaliação para o estudante?',
      description: 'Essa ação não poderá ser desfeita.',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (!confirmed) return;

    setSubmitting(true);
    const { error } = await client.mutate(`activities/${activityId}/activity_students/${id}`, {
      corrected: true,
      'comments-attributes': [{ body: newComment, 'activity-student-id': id, user_id: session.user.id }]
    });

    if (error) {
      toast.error('Não foi possível registrar a avaliação.');
      setSubmitting(false);
    } else {
      toast.success('Avaliação registrada com sucesso!');
      navigate(backRoute);
    }
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="rounded-2xl border border-neutral-lightest bg-white p-6 shadow-card">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <Badge
              variant={evaluated ? 'success-soft' : 'amber-soft'}
              className="mb-2 w-fit rounded-full px-2.5 py-1 text-xs font-bold"
            >
              {evaluated ? 'Avaliação realizada' : 'Aguardando avaliação'}
            </Badge>

            <h1
              className="mb-1 text-xl font-extrabold"
              style={{ color: '#222731' }}
            >
              {activityStudent.user?.name}
            </h1>
            <p
              data-testid="evaluation-activity-title"
              className="mb-0 text-sm font-semibold text-neutral-500"
            >
              {activity.title}
            </p>
          </div>

          {supportMaterialUrl && (
            <a
              data-testid="evaluation-support-material"
              href={supportMaterialUrl}
              target="_blank"
              rel="noreferrer"
            >
              <PlayfulCardButton
                variant="outline"
                icon={supportMaterial.url ? FiFileText : FiLink}
                className="w-auto shrink-0 px-5"
              >
                Material de apoio
              </PlayfulCardButton>
            </a>
          )}
        </div>

        <div className="mb-6 flex flex-col gap-1.5">
          <Label>Resposta enviada</Label>

          {studentComments.length > 0 ? (
            studentComments.map((comment, index) => (
              <p
                key={comment.id ?? index}
                data-testid="evaluation-student-comment"
                className="mb-0 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-dark"
              >
                {comment.body}
              </p>
            ))
          ) : (
            <p className="mb-0 text-sm text-neutral-500">Este aluno ainda não enviou uma resposta.</p>
          )}

          {attachment?.url && (
            <a
              data-testid="evaluation-attachment"
              href={attachment.url}
              target="_blank"
              rel="noreferrer"
              className="flex w-fit shrink-0 items-center justify-center gap-2 rounded-full border border-[var(--color-mission)] bg-white px-5 py-2.5 text-sm font-extrabold text-[var(--color-mission)] transition-transform duration-100 active:translate-y-px"
            >
              <FiExternalLink className="h-4 w-4" /> {attachment.url}
            </a>
          )}

          {!attachment?.url && attachment?.file && (
            <a
              data-testid="evaluation-attachment"
              href={attachment.file}
              target="_blank"
              rel="noreferrer"
            >
              <PlayfulCardButton
                variant="outline"
                icon={FiDownload}
                className="mt-1.5 w-fit shrink-0 px-5 py-2"
              >
                Ver arquivo enviado
              </PlayfulCardButton>
            </a>
          )}
        </div>

        {teacherComments.length > 0 && (
          <div className="mb-6 flex flex-col gap-1.5">
            <Label>Histórico de comentários</Label>
            {teacherComments.map((comment, index) => (
              <p
                key={comment.id ?? index}
                data-testid="evaluation-comment"
                className="mb-0 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-dark"
              >
                {comment.body}
              </p>
            ))}
          </div>
        )}

        {!evaluated && (
          <>
            <div className="mb-6 flex flex-col gap-1.5">
              <Label htmlFor="activity-feedback">Parecer do professor</Label>
              <Textarea
                id="activity-feedback"
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Escreva um retorno para o aluno..."
              />
            </div>

            <PlayfulCardButton
              onClick={handleMarkEvaluated}
              disabled={submitting}
              className="w-auto px-8"
            >
              Marcar como avaliado
            </PlayfulCardButton>
          </>
        )}
      </div>
    </DashboardContent>
  );
}
